import { Link } from 'react-router-dom';
import { formatMessage } from '@bookingdei/i18n/interpolate';
import { localiseDigits } from '@bookingdei/i18n/format-number';
import { toneColours } from '../../theme/tokens';
import { useLanguage } from '../../shell/language-context';
import { latestProof } from '../bookings/booking-payment';
import { rejectReasonLabel } from '../bookings/reject-reason';
import { ATTENTION_SHOWN } from './home-model';
import type { AttentionRow } from './home-model';
import { HomeIcon } from './home-icons';

// "Something needs you" (customer gap §A.3 H4; Others.dc.html `attention` part L437-487,
// rows built in Home.dc.html L694-720): tone-soft rows with a stripe, an icon tile, two
// lines and one call to action — solid for the urgent tones, outlined otherwise. Three
// rows at most; the rest are counted in "N more →" (→ My bookings).
export function HomeAttention({ rows }: { rows: AttentionRow[] }) {
  const { t, lang } = useLanguage();
  const shown = rows.slice(0, ATTENTION_SHOWN);
  const hidden = rows.length - shown.length;

  return (
    <section className="hm-card hm-att" data-motion="">
      <div className="hm-att__head">
        <span className="hm-att__title">{t('user.home.att.title')}</span>
        <span className="hm-att__count">{localiseDigits(lang, shown.length)}</span>
      </div>
      <div className="hm-att__rows">
        {shown.map((row) => {
          const b = row.booking;
          const service = b.items?.[0]?.name_snapshot ?? '';
          const bizName =
            (lang === 'bn' ? b.business?.name_bn : undefined) ?? b.business?.name ?? '';
          const place = [bizName, b.business?.area_name].filter(Boolean).join(' — ');
          const urgent = row.kind === 'proof';
          const tone = toneColours(urgent ? 'danger' : 'ok');
          const line1 =
            row.kind === 'proof'
              ? t('user.home.att.proof')
              : t(row.yesterday ? 'user.home.att.rate_yesterday' : 'user.home.att.rate');
          const line2 =
            row.kind === 'proof'
              ? [rejectReasonLabel(latestProof(b), t), b.code].filter(Boolean).join(' · ')
              : [service, place].filter(Boolean).join(' · ');
          const href =
            row.kind === 'proof' ? `/bookings?b=${encodeURIComponent(b.code)}` : '/reviews';
          return (
            <div
              key={`${row.kind}-${b.id}`}
              className="hm-att__row"
              data-motion=""
              style={{ background: tone.bg }}
            >
              <span
                className="hm-att__stripe"
                style={{ background: tone.dot }}
                aria-hidden="true"
              />
              <span className="hm-att__icon" style={{ color: tone.fg }}>
                <HomeIcon name={urgent ? 'warn' : 'star'} size={20} />
              </span>
              <div className="hm-att__text">
                <span className="hm-att__line1">{line1}</span>
                <span className="hm-att__line2">{line2}</span>
              </div>
              {urgent ? (
                <Link
                  className="hm-att__cta hm-att__cta--solid"
                  to={href}
                  style={{ background: tone.dot }}
                >
                  {t('user.home.hero.resubmit')}
                  <HomeIcon name="arrow" />
                </Link>
              ) : (
                <Link
                  className="hm-att__cta hm-att__cta--ghost"
                  to={href}
                  style={{ color: tone.fg, borderColor: tone.dot }}
                >
                  {t('user.home.att.rate_cta')}
                </Link>
              )}
            </div>
          );
        })}
      </div>
      {hidden > 0 && (
        <Link className="hm-att__more" to="/bookings">
          {formatMessage(lang, 'user.home.att.more', { n: hidden })}
        </Link>
      )}
    </section>
  );
}
